import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  GlobalStyles,
  Paper,
  Switch,
  FormControlLabel,
  Box,
} from '@mui/material';
import {
  ContentCopy,
  CheckCircle,
  BusinessCenter,
  AttachMoney,
  Settings,
  Info,
  Key,
  Refresh,
  ToggleOn,
  Notes,
  Warning,
} from '@mui/icons-material';
import { getToken } from 'src/auth';
import { api } from 'src/api/config';
import { connect } from 'react-redux';
import { MerchantFeePreview } from './MerchantFeePreview';

const BUSINESS_TYPE_OPTIONS = [
  { value: 'woocommerce', label: 'WooCommerce Store' },
  { value: 'shopify', label: 'Shopify Store' },
  { value: 'custom', label: 'Custom Integration' },
  { value: 'retail', label: 'Retail Store' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'services', label: 'Services' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'other', label: 'Other' },
];

const FEE_PAYER_OPTIONS = [
  { value: 'merchant', label: 'Merchant Pays All Fees' },
  { value: 'customer', label: 'Customer Pays All Fees' },
  { value: 'shared', label: 'Shared (Each Pays Own Fees)' },
];

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  business_type: 'other',
  website_url: '',
  fee_percentage: '1.00',
  flat_fee_usd: '0.00',
  customer_fee_percentage: '0.00',
  customer_fee_usd: '0.00',
  fee_payer: 'merchant',
  webhook_url: '',
  is_active: true,
  is_verified: false,
  notes: '',
};

// Aggressive CSS overrides to defeat Bulma - KEEPS BORDERS INTACT
const bulmaOverrides = {
  '.merchant-form-modal-wrapper': {
    // Reset ALL Bulma input styling
    '& input, & select, & textarea': {
      boxShadow: 'none !important',
      border: 'none !important',
      padding: '0 !important',
      height: 'auto !important',
      lineHeight: 'inherit !important',
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
      fontSize: 'inherit !important',
      fontFamily: 'inherit !important',
      width: 'auto !important',
      maxWidth: 'none !important',
      margin: '0 !important',
      borderRadius: '0 !important',
      '&:focus, &:active, &:hover': {
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important',
      },
    },
    // CRITICAL: Force MUI TextField borders to show - DO NOT REMOVE
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff !important',
      borderRadius: '8px !important',
      transition: 'all 0.2s ease-in-out !important',
      overflow: 'visible !important',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23) !important',
        borderWidth: '1px !important',
        borderStyle: 'solid !important',
        borderRadius: '8px !important',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.5) !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2563eb !important',
        borderWidth: '2px !important',
      },
      '&.Mui-error fieldset': {
        borderColor: '#dc2626 !important',
      },
      '& input': {
        textOverflow: 'clip !important',
      },
      '& textarea': {
        textOverflow: 'clip !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: '14px 16px !important',
      fontSize: '15px !important',
      lineHeight: '1.5 !important',
      color: '#1f2937 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '400 !important',
    },
    // URL fields need special handling for long text - allow scrolling
    '& input[type="url"]': {
      fontFamily: 'monospace !important',
      fontSize: '13px !important',
    },
    '& .MuiInputLabel-root': {
      fontSize: '15px !important',
      color: '#6b7280 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '500 !important',
      '&.Mui-focused': {
        color: '#2563eb !important',
        fontWeight: '600 !important',
      },
      '&.Mui-error': {
        color: '#dc2626 !important',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '13px !important',
      margin: '6px 14px 0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      lineHeight: '1.4 !important',
    },
    '& .MuiSelect-select': {
      padding: '14px 16px !important',
      paddingRight: '40px !important',
      backgroundColor: 'transparent !important',
      textOverflow: 'clip !important',
      whiteSpace: 'nowrap !important',
    },
    '& .MuiSelect-icon': {
      right: '12px !important',
      color: '#6b7280 !important',
    },
    // Dialog styling
    '& .MuiDialog-paper': {
      maxWidth: '850px !important',
      width: '100% !important',
      borderRadius: '12px !important',
    },
    '& .MuiDialogTitle-root': {
      padding: '28px 32px 20px !important',
      fontSize: '24px !important',
      fontWeight: '700 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
      background: 'linear-gradient(to bottom, #f9fafb, #ffffff) !important',
      borderBottom: '1px solid #e5e7eb !important',
    },
    '& .MuiDialogContent-root': {
      padding: '24px 32px !important',
      backgroundColor: '#f9fafb !important',
    },
    '& .MuiDialogActions-root': {
      padding: '20px 32px 24px !important',
      backgroundColor: '#ffffff !important',
      borderTop: '1px solid #e5e7eb !important',
      gap: '12px !important',
    },
    // Typography
    '& .MuiTypography-h6': {
      fontSize: '18px !important',
      fontWeight: '700 !important',
      margin: '0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
    },
    '& .MuiTypography-subtitle2': {
      fontSize: '14px !important',
      fontWeight: '700 !important',
      margin: '0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#374151 !important',
      textTransform: 'uppercase !important',
      letterSpacing: '0.5px !important',
    },
    '& .MuiTypography-body2': {
      fontSize: '14px !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#6b7280 !important',
      lineHeight: '1.6 !important',
    },
    // Buttons
    '& .MuiButton-root': {
      textTransform: 'none !important',
      fontSize: '15px !important',
      fontWeight: '600 !important',
      padding: '10px 24px !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      borderRadius: '8px !important',
      minHeight: '40px !important',
      '&.MuiButton-contained': {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important',
        },
      },
    },
    // Divider
    '& .MuiDivider-root': {
      margin: '0 !important',
      borderColor: '#e5e7eb !important',
    },
  },
};

function MerchantFormModalComponent({
  open,
  onClose,
  onSave,
  mode = 'create',
  account,
  merchantData,
  token,
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // API Key management state
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [currentApiKeyPrefix, setCurrentApiKeyPrefix] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && merchantData) {
        setFormData({
          name: merchantData.name || '',
          email: merchantData.email || '',
          phone: merchantData.phone || '',
          business_type: merchantData.business_type || 'other',
          website_url: merchantData.website_url || '',
          fee_percentage: merchantData.fee_percentage?.toString() || '1.00',
          flat_fee_usd: merchantData.flat_fee_usd?.toString() || '0.00',
          customer_fee_percentage: merchantData.customer_fee_percentage?.toString() || '0.00',
          customer_fee_usd: merchantData.customer_fee_usd?.toString() || '0.00',
          fee_payer: merchantData.fee_payer || 'merchant',
          webhook_url: merchantData.webhook_url || '',
          is_active: merchantData.is_active ?? true,
          is_verified: merchantData.is_verified ?? false,
          notes: merchantData.notes || '',
        });
      } else if (mode === 'create' && account) {
        setFormData({
          ...initialFormState,
          name: account.account_name || '',
          email: account.account_email || '',
          phone: account.account_phone || '',
        });
      } else {
        setFormData(initialFormState);
      }
      setError(null);
      setValidationErrors({});
      setTouchedFields({});
      setCopied(false);
      setNewApiKey(null);
      setCurrentApiKeyPrefix(null);
    }
  }, [open, mode, account, merchantData]);

  // Memoized preview parameters for live fee preview
  const feePreviewParams = useMemo(() => ({
    merchantFeePct: parseFloat(formData.fee_percentage) || 0,
    merchantFeeUsd: parseFloat(formData.flat_fee_usd) || 0,
    customerFeePct: parseFloat(formData.customer_fee_percentage) || 0,
    customerFeeUsd: parseFloat(formData.customer_fee_usd) || 0,
    feePayer: formData.fee_payer,
  }), [formData.fee_percentage, formData.flat_fee_usd, formData.customer_fee_percentage, formData.customer_fee_usd, formData.fee_payer]);

  const validateFees = (updatingField = null, updatingValue = null) => {
    const errors = {};

    // Use the updating value if provided, otherwise use formData
    const getValue = (fieldName) => {
      if (updatingField === fieldName) {
        return updatingValue;
      }
      return formData[fieldName];
    };

    // Helper to safely parse fee values
    const parseFee = (value) => {
      if (value === null || value === undefined || value === '') {
        return 0;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const feePayer = getValue('fee_payer');

    // Parse all fee values as numbers, handling empty strings and invalid values
    const merchantPct = parseFee(getValue('fee_percentage'));
    const merchantFlat = parseFee(getValue('flat_fee_usd'));
    const customerPct = parseFee(getValue('customer_fee_percentage'));
    const customerFlat = parseFee(getValue('customer_fee_usd'));

    if (feePayer === 'merchant') {
      // Merchant pays: at least one merchant fee must be non-zero
      if (merchantPct === 0 && merchantFlat === 0) {
        errors.fee_percentage = 'Merchant must have at least one fee';
        errors.flat_fee_usd = 'Merchant must have at least one fee';
      }
    } else if (feePayer === 'customer') {
      // Customer pays: at least one customer fee must be non-zero
      if (customerPct === 0 && customerFlat === 0) {
        errors.customer_fee_percentage = 'Customer must have at least one fee';
        errors.customer_fee_usd = 'Customer must have at least one fee';
      }
    } else if (feePayer === 'shared') {
      // Shared: both sides need at least one non-zero fee
      if (merchantPct === 0 && merchantFlat === 0) {
        errors.fee_percentage = 'Merchant must have at least one fee for shared payment';
        errors.flat_fee_usd = 'Merchant must have at least one fee for shared payment';
      }
      if (customerPct === 0 && customerFlat === 0) {
        errors.customer_fee_percentage = 'Customer must have at least one fee for shared payment';
        errors.customer_fee_usd = 'Customer must have at least one fee for shared payment';
      }
    }

    return errors;
  };

  const validateField = (field, value) => {
    const errors = {};

    if (field === 'name' && touchedFields.name) {
      if (!value || !value.trim()) {
        errors.name = 'Merchant name is required';
      } else if (value.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters';
      }
    }

    if (field === 'email') {
      if (!value || !value.trim()) {
        if (touchedFields.email) {
          errors.email = 'Email address is required';
        }
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
      }
    }

    if (field === 'website_url' && value && value.trim()) {
      try {
        new URL(value);
        if (!value.startsWith('http://') && !value.startsWith('https://')) {
          errors.website_url = 'URL must start with http:// or https://';
        }
      } catch {
        errors.website_url = 'Please enter a valid URL (e.g., https://example.com)';
      }
    }

    if (field === 'webhook_url' && value && value.trim()) {
      try {
        new URL(value);
        if (!value.startsWith('http://') && !value.startsWith('https://')) {
          errors.webhook_url = 'URL must start with http:// or https://';
        }
      } catch {
        errors.webhook_url = 'Please enter a valid URL';
      }
    }

    if (field === 'phone' && value && value.trim()) {
      const phoneRegex = /^[\d\s()+-]+$/;
      const digits = value.replace(/\D/g, '');
      if (!phoneRegex.test(value)) {
        errors.phone = 'Phone can only contain digits, spaces, +, -, (, )';
      } else if (digits.length < 10) {
        errors.phone = 'Phone number must have at least 10 digits';
      }
    }

    if (field === 'fee_percentage' && value) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0 || num > 100) {
        errors.fee_percentage = 'Must be between 0 and 100';
      }
    }

    if (field === 'flat_fee_usd' && value) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        errors.flat_fee_usd = 'Must be a positive number';
      }
    }

    if (field === 'customer_fee_percentage' && value) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0 || num > 100) {
        errors.customer_fee_percentage = 'Must be between 0 and 100';
      }
    }

    if (field === 'customer_fee_usd' && value) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        errors.customer_fee_usd = 'Must be a positive number';
      }
    }

    // Add conditional fee validation for any fee-related field
    const feeFields = ['fee_percentage', 'flat_fee_usd', 'customer_fee_percentage', 'customer_fee_usd', 'fee_payer'];
    if (feeFields.includes(field)) {
      const feeErrors = validateFees(field, value);
      Object.assign(errors, feeErrors);
    }

    return errors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Real-time validation
    const errors = validateField(field, value);

    setValidationErrors((prev) => {
      const newErrors = { ...prev };

      // For fee-related fields, always clear all fee errors first, then re-apply if needed
      const feeFields = ['fee_percentage', 'flat_fee_usd', 'customer_fee_percentage', 'customer_fee_usd', 'fee_payer'];
      if (feeFields.includes(field)) {
        // Clear all fee-related errors first
        delete newErrors.fee_percentage;
        delete newErrors.flat_fee_usd;
        delete newErrors.customer_fee_percentage;
        delete newErrors.customer_fee_usd;

        // Then re-apply any errors that still exist
        if (Object.keys(errors).length > 0) {
          Object.assign(newErrors, errors);
        }
      } else {
        // For non-fee fields, use the original logic
        if (Object.keys(errors).length > 0) {
          Object.assign(newErrors, errors);
        } else {
          delete newErrors[field];
        }
      }

      return newErrors;
    });
  };

  const handleBlur = (field) => () => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    const value = formData[field];
    const errors = validateField(field, value);
    setValidationErrors((prev) => ({
      ...prev,
      ...errors,
    }));
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    const allFields = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouchedFields(allFields);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Merchant name is required');
      setValidationErrors(prev => ({ ...prev, name: 'Merchant name is required' }));
      return;
    }
    if (!formData.email.trim()) {
      setError('Email address is required');
      setValidationErrors(prev => ({ ...prev, email: 'Email address is required' }));
      return;
    }

    // Validate all fields
    const allErrors = {};
    Object.entries(formData).forEach(([field, value]) => {
      const fieldErrors = validateField(field, value);
      Object.assign(allErrors, fieldErrors);
    });

    // Add comprehensive fee validation
    const feeValidationErrors = validateFees();
    Object.assign(allErrors, feeValidationErrors);

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      setError('Please fix the validation errors before submitting');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await onSave(formData);
      // If this was a create and we got an API key back, show it
      if (mode === 'create' && result?.api_key) {
        setNewApiKey(result.api_key);
        setApiKeyDialogOpen(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to save merchant');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, showCopiedState = false) => {
    navigator.clipboard.writeText(text);
    if (showCopiedState) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle switch changes for boolean fields
  const handleSwitchChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  // Handle API Key regeneration
  const handleRegenerateAPIKey = () => {
    setConfirmRegenerateOpen(true);
  };

  const confirmRegenerate = async () => {
    setRegenerating(true);
    setConfirmRegenerateOpen(false);

    try {
      const response = await fetch(
        `${api.hostname}/api/v1/pos/merchants/${account.id}/regenerate-api-key/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to regenerate API key');
      }

      const data = await response.json();
      setNewApiKey(data.api_key);
      setCurrentApiKeyPrefix(data.api_key_prefix);
      setApiKeyDialogOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  // Handle closing the API key dialog
  const handleCloseApiKeyDialog = () => {
    setApiKeyDialogOpen(false);
    setNewApiKey(null);
    setCopied(false);
    // Close the main modal if this was from create
    if (mode === 'create') {
      onClose();
    }
  };

  const isCreate = mode === 'create';
  const title = isCreate ? 'Enable POS Payments' : 'Merchant Configuration';

  return (
    <>
      <GlobalStyles styles={bulmaOverrides} />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        className="merchant-form-modal-wrapper"
      >
        <DialogTitle>
          {title}
          {account && (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500 }}>
              {account.account_name} • ID: {account.id} • {account.fi_name}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                mb: 3,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Business Information */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <BusinessCenter sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Business Information</Typography>
            </div>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Merchant Name"
                value={formData.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                required
                placeholder="Enter business name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Business Type"
                value={formData.business_type}
                onChange={handleChange('business_type')}
              >
                {BUSINESS_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                error={!!validationErrors.email}
                helperText={validationErrors.email || 'Primary contact email'}
                required
                placeholder="email@example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={handleChange('phone')}
                onBlur={handleBlur('phone')}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone || 'Format: +1 (555) 123-4567'}
                placeholder="+1 (555) 123-4567"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website URL"
                type="url"
                value={formData.website_url}
                onChange={handleChange('website_url')}
                onBlur={handleBlur('website_url')}
                error={!!validationErrors.website_url}
                helperText={validationErrors.website_url || 'Your business website'}
                placeholder="https://example.com"
              />
              </Grid>
            </Grid>
          </Paper>

          {/* Fee Configuration */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <AttachMoney sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Fee Configuration</Typography>
            </div>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Who Pays the Fees?"
                value={formData.fee_payer}
                onChange={handleChange('fee_payer')}
              >
                {FEE_PAYER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              </Grid>
            </Grid>

            {/* Show info when fee_payer is selected and there are unused fees */}
            {formData.fee_payer === 'merchant' && (
              (parseFloat(formData.customer_fee_percentage) > 0 || parseFloat(formData.customer_fee_usd) > 0)
            ) && (
              <Alert severity="info" sx={{ mt: 2, fontSize: '13px', borderRadius: '8px' }}>
                Customer fees will not be applied when merchant pays all fees
              </Alert>
            )}
            {formData.fee_payer === 'customer' && (
              (parseFloat(formData.fee_percentage) > 0 || parseFloat(formData.flat_fee_usd) > 0)
            ) && (
              <Alert severity="info" sx={{ mt: 2, fontSize: '13px', borderRadius: '8px' }}>
                Merchant fees will not be applied when customer pays all fees
              </Alert>
            )}

            <Divider sx={{ my: 4 }} />

            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Merchant Percentage Fee"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                value={formData.fee_percentage}
                onChange={handleChange('fee_percentage')}
                onBlur={handleBlur('fee_percentage')}
                error={!!validationErrors.fee_percentage}
                helperText={validationErrors.fee_percentage}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Merchant Flat Fee"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.flat_fee_usd}
                onChange={handleChange('flat_fee_usd')}
                onBlur={handleBlur('flat_fee_usd')}
                error={!!validationErrors.flat_fee_usd}
                helperText={validationErrors.flat_fee_usd}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }
                }}
              />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Percentage Fee"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                value={formData.customer_fee_percentage}
                onChange={handleChange('customer_fee_percentage')}
                onBlur={handleBlur('customer_fee_percentage')}
                error={!!validationErrors.customer_fee_percentage}
                helperText={validationErrors.customer_fee_percentage}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Flat Fee"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.customer_fee_usd}
                onChange={handleChange('customer_fee_usd')}
                onBlur={handleBlur('customer_fee_usd')}
                error={!!validationErrors.customer_fee_usd}
                helperText={validationErrors.customer_fee_usd}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }
                }}
              />
              </Grid>
            </Grid>

            {/* Live Fee Preview */}
            <MerchantFeePreview
              merchantFeePct={feePreviewParams.merchantFeePct}
              merchantFeeUsd={feePreviewParams.merchantFeeUsd}
              customerFeePct={feePreviewParams.customerFeePct}
              customerFeeUsd={feePreviewParams.customerFeeUsd}
              feePayer={feePreviewParams.feePayer}
              transactionAmount={100}
            />
          </Paper>

          {/* Integration Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <Settings sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Integration Settings</Typography>
            </div>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  type="url"
                  value={formData.webhook_url}
                  onChange={handleChange('webhook_url')}
                  onBlur={handleBlur('webhook_url')}
                  error={!!validationErrors.webhook_url}
                  helperText={validationErrors.webhook_url}
                  placeholder="https://your-site.com/api/webhook"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Merchant Status (Edit Mode Only) */}
          {mode === 'edit' && merchantData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                }}>
                  <ToggleOn sx={{ fontSize: '22px' }} />
                </div>
                <Typography variant="h6">Merchant Status</Typography>
              </div>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, bgcolor: formData.is_active ? '#ecfdf5' : '#fef2f2', borderRadius: '8px' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_active}
                          onChange={handleSwitchChange('is_active')}
                          color="success"
                        />
                      }
                      label={<Typography fontWeight="medium">Active</Typography>}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                      Inactive merchants cannot process payments
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, bgcolor: formData.is_verified ? '#eff6ff' : '#f9fafb', borderRadius: '8px' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_verified}
                          onChange={handleSwitchChange('is_verified')}
                          color="info"
                        />
                      }
                      label={<Typography fontWeight="medium">Verified</Typography>}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                      Business/email verification status
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* API Key Management (Edit Mode Only) */}
          {mode === 'edit' && merchantData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                }}>
                  <Key sx={{ fontSize: '22px' }} />
                </div>
                <Typography variant="h6">API Key</Typography>
              </div>
              <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key Prefix"
                    value={currentApiKeyPrefix || merchantData.api_key_prefix || 'Not generated'}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Only the prefix is stored. Full key shown only at creation or regeneration.">
                              <Info fontSize="small" color="action" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace !important',
                        backgroundColor: '#f5f5f5 !important',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={regenerating ? <CircularProgress size={18} /> : <Refresh />}
                    onClick={handleRegenerateAPIKey}
                    disabled={regenerating}
                  >
                    {regenerating ? 'Regenerating...' : 'Regenerate API Key'}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px' }}>
                    <strong>Warning:</strong> Regenerating the API key will invalidate the current key.
                    Any integrations using the old key will stop working immediately.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Internal Notes */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <Notes sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Internal Notes</Typography>
            </div>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={3}
              placeholder="Internal notes about this merchant (not visible to merchant)..."
            />
          </Paper>

          {/* System Information (Edit Mode Only) */}
          {mode === 'edit' && merchantData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                }}>
                  <Info sx={{ fontSize: '22px' }} />
                </div>
                <Typography variant="h6">System Information</Typography>
              </div>
              <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Merchant ID"
                    value={merchantData.id || '—'}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: merchantData.id && (
                          <InputAdornment position="end">
                            <Tooltip title="Copy">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(merchantData.id.toString())}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace !important',
                        backgroundColor: '#f5f5f5 !important',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={merchantData.slug || '—'}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: merchantData.slug && (
                          <InputAdornment position="end">
                            <Tooltip title="Copy">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(merchantData.slug)}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace !important',
                        backgroundColor: '#f5f5f5 !important',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Created"
                    value={merchantData.created_at ? new Date(merchantData.created_at).toLocaleString() : '—'}
                    slotProps={{
                      input: {
                        readOnly: true,
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5 !important',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Webhook Secret"
                    value={merchantData.webhook_secret || '—'}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: merchantData.webhook_secret && (
                          <InputAdornment position="end">
                            <Tooltip title="Copy">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(merchantData.webhook_secret)}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace !important',
                        backgroundColor: '#f5f5f5 !important',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
                color: '#111827',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CheckCircle />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af',
              }
            }}
          >
            {saving ? 'Saving...' : isCreate ? 'Enable Payments' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Display Dialog */}
      <Dialog open={apiKeyDialogOpen} maxWidth="sm" fullWidth className="merchant-form-modal-wrapper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          {mode === 'create' ? 'Merchant Created Successfully' : 'API Key Regenerated'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
            <strong>Important:</strong> This is the only time the API key will be shown.
            Please copy and store it securely. It cannot be retrieved later.
          </Alert>
          <TextField
            fullWidth
            label="API Key"
            value={newApiKey || ''}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                      <IconButton onClick={() => copyToClipboard(newApiKey, true)}>
                        {copied ? <CheckCircle color="success" /> : <ContentCopy />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace !important',
                fontSize: '14px !important',
              }
            }}
          />
          {copied && (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
              Copied to clipboard!
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseApiKeyDialog}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              },
            }}
          >
            I've Saved the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <Dialog open={confirmRegenerateOpen} maxWidth="sm" fullWidth className="merchant-form-modal-wrapper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Regenerate API Key?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Regenerating the API key will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            <li><Typography variant="body2">Invalidate the current API key immediately</Typography></li>
            <li><Typography variant="body2">Break any existing integrations using the old key</Typography></li>
            <li><Typography variant="body2">Generate a new key that must be configured in all systems</Typography></li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenerateOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmRegenerate}
            variant="contained"
            color="error"
          >
            Regenerate Key
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Connect to Redux for token
const mapStateToProps = (state) => ({
  token: getToken(state),
});

export const MerchantFormModal = connect(mapStateToProps)(MerchantFormModalComponent);
